import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { verifyContractorMasterByCode } from "../../../infrastructure/api/contractorMasterVerificationApi";

const Field = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body1" sx={{ wordBreak: "break-word" }}>
      {value || "—"}
    </Typography>
  </Box>
);

export default function ContractorMasterPublicVerifyPage() {
  const { verificationCode } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError("");
      setResult(null);
      try {
        if (!verificationCode?.trim()) {
          throw new Error("کد اعتبارسنجی نامعتبر است.");
        }
        const data = await verifyContractorMasterByCode(verificationCode);
        if (!cancelled) setResult(data);
      } catch (err) {
        if (!cancelled) {
          setError(
            err?.response?.data?.message ||
              err?.message ||
              "بررسی اعتبار قرارداد انجام نشد."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [verificationCode]);

  const isValid = Boolean(result?.isValid);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#F3F6F4",
        py: { xs: 4, md: 6 },
      }}
    >
      <Container maxWidth="sm">
        <Stack spacing={2} sx={{ mb: 3 }}>
          <Typography variant="h5" fontWeight={700}>
            بررسی اعتبار قرارداد پیمانکار
          </Typography>
          <Typography variant="body2" color="text.secondary">
            پلتفرم سولارنت — نتیجه اعتبارسنجی عمومی قرارداد master
          </Typography>
        </Stack>

        <Card elevation={0} sx={{ border: "1px solid", borderColor: "divider" }}>
          <CardContent>
            {loading ? (
              <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
                <CircularProgress size={36} />
                <Typography color="text.secondary">در حال بررسی...</Typography>
              </Stack>
            ) : error ? (
              <Alert severity="error" icon={<ErrorOutlineIcon />}>
                {error}
              </Alert>
            ) : (
              <Stack spacing={2}>
                <Alert
                  severity={isValid ? "success" : "warning"}
                  icon={isValid ? <CheckCircleOutlineIcon /> : <ErrorOutlineIcon />}
                >
                  {result?.message ||
                    (isValid
                      ? "قرارداد معتبر است."
                      : "قرارداد معتبر نیست یا یافت نشد.")}
                </Alert>
                <Divider />
                <Field label="کد اعتبارسنجی" value={verificationCode} />
                <Field label="شماره قرارداد" value={result?.contractNumber} />
                <Field label="نسخه" value={result?.versionNumber} />
                <Field label="وضعیت" value={result?.statusTitle || result?.status} />
                <Field label="وضعیت امضا" value={result?.signatureStatus} />
                <Field label="طرف سولارنت" value={result?.solarentPartyName} />
                <Field
                  label="طرف پیمانکار"
                  value={result?.contractorPartyDisplayName}
                />
                <Field label="سلامت فایل" value={result?.integrityStatus} />
                <Field label="اثرانگشت PDF (SHA-256)" value={result?.pdfSha256} />
              </Stack>
            )}
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}
